import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { useTheme } from '../contexts/ThemeContext';
import { IconTrendingUp, IconCalendarStats, IconArrowUpRight } from '@tabler/icons-react';
import { iconStroke } from '../config/config';

export default function WeeklySalesChart({ weeklySales, currency }) {
  const { theme } = useTheme();
  const isDark = theme === 'black';

  const daysData = weeklySales?.days || [];
  const totalRevenue = weeklySales?.totalWeeklyRevenue || 0;

  // Format currency symbol
  const symbol = currency?.symbol || '$';

  // Prepare series & categories
  const categories = daysData.map(d => {
    // Format date like "Mon (Sep 21)" or just "Mon"
    const dateParts = d.date ? d.date.split('-') : [];
    const formattedShort = dateParts.length === 3 ? `${Number(dateParts[1])}/${Number(dateParts[2])}` : '';
    return `${d.day} ${formattedShort}`.trim();
  });

  const seriesData = daysData.map(d => Number(d.sales || 0));

  // Find max day
  let highestDay = null;
  let highestAmount = 0;
  daysData.forEach(d => {
    if (d.sales > highestAmount) {
      highestAmount = d.sales;
      highestDay = d.day;
    }
  });

  const avgDaily = daysData.length > 0 ? totalRevenue / daysData.length : 0;

  const chartOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      background: 'transparent',
      fontFamily: 'inherit',
      animations: {
        enabled: false,
      },
      redrawOnParentResize: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        borderRadiusApplication: 'end',
        columnWidth: '45%',
        distributed: false,
        dataLabels: {
          position: 'top',
        },
      }
    },
    colors: [isDark ? '#34D399' : '#059669'],
    fill: {
      type: 'gradient',
      gradient: {
        shade: isDark ? 'dark' : 'light',
        type: 'vertical',
        shadeIntensity: 0.3,
        gradientToColors: [isDark ? '#059669' : '#10B981'],
        inverseColors: false,
        opacityFrom: 0.95,
        opacityTo: 0.85,
        stops: [0, 100]
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        if (!val || val === 0) return '';
        const num = Number(val || 0);
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
        return Math.round(num);
      },
      offsetY: -22,
      style: {
        fontSize: '11px',
        fontWeight: 700,
        colors: [isDark ? '#E5E7EB' : '#374151']
      }
    },
    grid: {
      borderColor: isDark ? '#374151' : '#E5E7EB',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 10, bottom: 0, left: 10 }
    },
    xaxis: {
      categories: categories,
      labels: {
        style: {
          colors: isDark ? '#9CA3AF' : '#4B5563',
          fontSize: '12px',
          fontWeight: 600,
        }
      },
      axisBorder: {
        show: true,
        color: isDark ? '#4B5563' : '#E5E7EB'
      },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          const num = Number(val || 0);
          if (num >= 1000000) return `${symbol}${(num / 1000000).toFixed(1)}M`;
          if (num >= 1000) return `${symbol}${(num / 1000).toFixed(0)}k`;
          return `${symbol}${num}`;
        },
        style: {
          colors: isDark ? '#9CA3AF' : '#6B7280',
          fontSize: '11px',
          fontWeight: 500
        }
      }
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: {
        formatter: function (val, opts) {
          const idx = opts?.dataPointIndex;
          const dayInfo = (idx !== undefined && idx !== null && daysData) ? daysData[idx] : null;
          const invoicesTxt = dayInfo?.invoices ? ` (${dayInfo.invoices} invoices)` : '';
          const numVal = Number(val || 0);
          return `${symbol}${numVal.toLocaleString()}${invoicesTxt}`;
        }
      }
    }
  };

  const chartSeries = [
    {
      name: 'Sales Revenue',
      data: seriesData
    }
  ];

  return (
    <div className="border border-restro-border-green rounded-[2rem] p-6 lg:p-8 bg-white dark:bg-restro-card-bg shadow-sm mt-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-restro-green/10 text-restro-green dark:bg-restro-green/20">
              <IconCalendarStats size={22} stroke={iconStroke} />
            </div>
            <div>
              <h3 className="text-xl font-black text-restro-text">
                Weekly Sales (This Week)
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Day-by-day sales revenue & volume for the current week
              </p>
            </div>
          </div>
        </div>

        {/* Quick Highlights */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="px-4 py-2 rounded-2xl bg-restro-green/5 dark:bg-restro-green/10 border border-restro-border-green flex items-center gap-2">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Total Sales:</span>
            <span className="text-base font-black text-restro-green">
              {symbol}{Number(totalRevenue || 0).toLocaleString()}
            </span>
          </div>

          {avgDaily > 0 && (
            <div className="px-4 py-2 rounded-2xl bg-restro-bg-gray dark:bg-neutral-800 border border-restro-border-green flex items-center gap-2">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Daily Avg:</span>
              <span className="text-sm font-bold text-restro-text">
                {symbol}{Math.round(avgDaily).toLocaleString()}
              </span>
            </div>
          )}

          {highestDay && highestAmount > 0 && (
            <div className="hidden md:flex px-4 py-2 rounded-2xl bg-amber-500/10 border border-amber-500/30 items-center gap-2">
              <IconTrendingUp size={16} className="text-amber-500" />
              <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400">
                Peak: {highestDay} ({symbol}{Number(highestAmount).toLocaleString()})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Bar Chart */}
      <div className="w-full min-h-[320px]">
        {daysData.length === 0 ? (
          <div className="flex items-center justify-center h-[320px] text-sm text-gray-400">
            No weekly sales recorded yet.
          </div>
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={320}
          />
        )}
      </div>
    </div>
  );
}
